import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../Component/axios_Interceptor/api';
import url, { SERVER_ADDRESS } from '../../../Component/server';

type Reservation = { seq: number; live_title: string; live_start: string; live_finish: string };
type BankLive = { live_seq: number; live_title: string; live_start: string; order_count: number };
type FailedLive = { dispatch_id: number; live_seq: number; live_title: string; live_start: string; failure_count: number };
type Operations = {
  reserved_lives: Reservation[] | null;
  pending_inquiries: number | null;
  bank_pending_lives: BankLive[] | null;
  failed_statements: FailedLive[] | null;
};
type Dispatch = {
  dispatch_id: number; fail_cnt: number; pending_cnt: number; retryable_cnt: number; retry_coins: number;
  failures: { id: number; state: string; message: string }[];
};
const count = (value: number) => value.toLocaleString('ko-KR');
const date = (value: string) => new Date(value).toLocaleString('ko-KR', {
  timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
});

function StatementFailure({ live, onChanged }: { live: FailedLive; onChanged: (message: string) => void }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [result, setResult] = useState<Dispatch | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [sent, setSent] = useState(false);
  const running = useRef(false);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const load = async () => {
    if (running.current) return;
    running.current = true; setBusy(true); setError('');
    try {
      const { data } = await api.get(`${SERVER_ADDRESS}notifications/${live.dispatch_id}`);
      if (!Array.isArray(data?.failures) || !Number.isInteger(data?.retryable_cnt)) throw new Error();
      if (active.current) setResult(data);
    } catch { if (active.current) setError('발송 결과를 불러오지 못했습니다. 다시 확인해주세요.'); }
    finally { running.current = false; if (active.current) setBusy(false); }
  };
  const retry = async () => {
    if (running.current || !result?.retryable_cnt) return;
    running.current = true; setBusy(true); setError('');
    try {
      const { data } = await api.post(url.Communication_url.send_statement, {
        live_seq: live.live_seq, retry_failed: true, order_count: result.retryable_cnt, coin: result.retry_coins || 0,
      });
      if (data?.dispatch_id !== live.dispatch_id || !Number.isInteger(data.retried_cnt)) throw new Error('재발송 접수를 확인하지 못했습니다. 결과를 다시 확인해주세요.');
      if (active.current) { setResult(data); setConfirm(false); setSent(data.retried_cnt > 0); }
      onChanged(data.retried_cnt > 0 ? `${live.live_title} · 실패 ${count(data.retried_cnt)}건의 재발송을 접수했습니다.` : '현재 재발송 가능한 건이 없습니다. 발송 결과를 다시 확인해주세요.');
    } catch (cause: any) {
      if (active.current) setError(cause?.response?.data?.message || cause.message || '재발송을 접수하지 못했습니다.');
    } finally { running.current = false; if (active.current) setBusy(false); }
  };
  return <li>
    <button type="button" className="operation-row" aria-expanded={expanded} aria-controls={`dashboard-failure-${live.dispatch_id}`} onClick={() => {
      setExpanded(!expanded); setConfirm(false); if (!expanded) void load();
    }}><span><strong title={live.live_title}>{live.live_title}</strong><small>{date(live.live_start)}</small></span><span className="row-action">{count(Number(live.failure_count))}건 · 실패 확인</span></button>
    {expanded && <div className="failure-detail" id={`dashboard-failure-${live.dispatch_id}`} aria-busy={busy}>
      {busy && <p role="status">처리 중…</p>}
      {error && <p role="alert">{error}</p>}
      {sent && <p role="status">실패 건 재발송을 접수했습니다.</p>}
      {result && <>
        <ul>{result.failures.map(failure => <li key={failure.id}>{failure.message || '발송 실패'}{failure.state === 'unknown' ? ' (결과 확인 중)' : ''}</li>)}</ul>
        {result.retryable_cnt > 0 ? <>
          <p>재발송 가능 {count(result.retryable_cnt)}건 · 예상 사용 {count(result.retry_coins || 0)} C</p>
          {confirm ? <div className="confirmation"><p>실패한 {count(result.retryable_cnt)}건을 다시 보내시겠습니까?</p>
            <button type="button" disabled={busy} onClick={() => void retry()}>재발송 확인</button>
            <button type="button" disabled={busy} onClick={() => setConfirm(false)}>취소</button>
          </div> : <button type="button" disabled={busy} onClick={() => setConfirm(true)}>실패 건 재발송</button>}
        </> : !sent && <p>재발송 가능한 건이 없습니다. 연락처·주문 상태·발송 결과를 확인해주세요.</p>}
      </>}
      <div className="detail-actions"><button type="button" disabled={busy} onClick={() => void load()}>결과 다시 확인</button>
        <button type="button" onClick={() => navigate(`/admin/orderlist/all?live_seq=${live.live_seq}`)}>해당 방송 주문 보기</button></div>
    </div>}
  </li>;
}

export default function DashboardOperations({ refreshKey = 0 }: { refreshKey?: number }) {
  const navigate = useNavigate();
  const [data, setData] = useState<Operations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [showBanks, setShowBanks] = useState(false);
  const [showFailures, setShowFailures] = useState(false);
  const [category, setCategory] = useState('banks');
  const [updated, setUpdated] = useState<Date | null>(null);
  const request = useRef(0);
  const refresh = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    try {
      const response = await api.get(`${SERVER_ADDRESS}orders/dashboard-operations`);
      const next = response.data;
      if (!next || !['reserved_lives', 'bank_pending_lives', 'failed_statements'].every(key => next[key] === null || Array.isArray(next[key]))
        || !(next.pending_inquiries === null || Number.isInteger(next.pending_inquiries))) throw new Error();
      if (request.current === id) { setData(next); setError(false); setUpdated(new Date()); }
    } catch { if (request.current === id) setError(true); }
    finally { if (request.current === id) setLoading(false); }
  }, []);
  useEffect(() => {
    void refresh();
    const onFocus = () => { if (document.visibilityState !== 'hidden') void refresh(); };
    const timer = window.setInterval(onFocus, 30000);
    window.addEventListener('focus', onFocus);
    return () => { request.current += 1; window.clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [refresh, refreshKey]);
  const state = (value: unknown, empty: string) => value === null || value === undefined
    ? <p className="section-state" role="status">{loading ? '불러오는 중…' : '정보를 불러오지 못했습니다.'}</p>
    : <p className="section-state">{empty}</p>;
  const bankCount = data?.bank_pending_lives?.reduce((total, live) => total + Number(live.order_count), 0);
  const failureCount = data?.failed_statements?.reduce((total, live) => total + Number(live.failure_count), 0);
  const categories = [
    { key: 'inquiries', label: '대기 중인 문의', total: data?.pending_inquiries },
    { key: 'banks', label: '무통장 입금 확인', total: bankCount },
    { key: 'failures', label: '정산서 발송 실패', total: failureCount },
  ];
  const noAlerts = data?.pending_inquiries === 0 && bankCount === 0 && failureCount === 0;
  return <OperationsArea aria-label="방송 예약 및 처리 알림" aria-busy={loading}>
    <div className="operations-grid">
      <section className="operations-panel" aria-label="처리할 알림">
        <div className="operations-heading"><div><h2>방송 예약 및 처리 알림</h2><p>전체 기간의 미처리 내역{updated ? ` · ${updated.toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit' })} 확인` : ''}</p></div>
          <button type="button" disabled={loading} onClick={() => void refresh()}>알림 새로고침</button></div>
        {error && <p className="operations-error" role="status">예약·알림을 불러오지 못했습니다.{data ? ' 마지막으로 확인한 내용입니다.' : ''} 새로고침으로 다시 확인해주세요.</p>}
        {feedback && <p className="operations-feedback" role="status">{feedback}</p>}
        <div data-admin-tabs="" className="alert-tabs" role="tablist" aria-label="알림 종류">
          {categories.map((item, index) => <button type="button" role="tab" key={item.key} id={`dashboard-alert-${item.key}`}
            aria-controls="dashboard-alert-panel" aria-selected={category === item.key} tabIndex={category === item.key ? 0 : -1}
            onClick={() => setCategory(item.key)} onKeyDown={event => {
              const next = event.key === 'ArrowRight' ? (index + 1) % categories.length
                : event.key === 'ArrowLeft' ? (index + categories.length - 1) % categories.length
                : event.key === 'Home' ? 0 : event.key === 'End' ? categories.length - 1 : -1;
              if (next < 0) return;
              event.preventDefault();
              setCategory(categories[next].key);
              event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next].focus();
            }}>
            <span>{item.label}</span><span className={item.key === 'failures' && !!item.total ? 'tab-count failure-count' : 'tab-count'}>
              {item.total == null ? loading ? '—' : '확인 필요' : `${count(item.total)}건`}
            </span>
          </button>)}
        </div>
        <div role="tabpanel" id="dashboard-alert-panel" aria-labelledby={`dashboard-alert-${category}`}>
        {noAlerts ? <p className="section-state">지금 처리할 알림이 없습니다.</p> : <>
        {category === 'inquiries' && (data?.pending_inquiries ? <button type="button" className="operation-row inquiry" onClick={() => navigate('/admin/customer-board?status=open&all_dates=1')}>
          <span><strong>대기 중인 문의</strong><small>답변을 기다리는 고객 문의</small></span><span className="row-action">{count(data.pending_inquiries)}건 · 문의 확인 →</span>
        </button> : state(data?.pending_inquiries, '대기 중인 문의가 없습니다.'))}
        {category === 'banks' && (data?.bank_pending_lives == null ? state(null, '') : bankCount! > 0 ? <div className="alert-group">
          <h4>무통장 입금 확인 <span>{count(bankCount!)}건</span></h4><p>통장 입금 내역과 대조해 수동으로 결제완료 처리해주세요.</p>
          <ul className="operation-list">{(showBanks ? data.bank_pending_lives : data.bank_pending_lives.slice(0, 3)).map(live => <li key={live.live_seq}>
            <button type="button" className="operation-row" onClick={() => navigate(`/admin/orderlist/payment-wait?live_seq=${live.live_seq}&bank_pending=true`)}><span><strong title={live.live_title}>{live.live_title}</strong><small>{date(live.live_start)}</small></span><span className="row-action">{count(Number(live.order_count))}건 · 입금 확인 →</span></button>
          </li>)}</ul>{data.bank_pending_lives.length > 3 && <button type="button" className="show-more" aria-expanded={showBanks} onClick={() => setShowBanks(!showBanks)}>{showBanks ? '접기' : '입금 대기 방송 모두 보기'}</button>}
        </div> : state(data.bank_pending_lives, '입금 대기 방송이 없습니다.'))}
        {category === 'failures' && (data?.failed_statements == null ? state(null, '') : failureCount! > 0 ? <div className="alert-group">
          <h4>정산서 발송 실패 <span>{count(failureCount!)}건</span></h4><p>발송 결과를 확인하고 실패 건을 처리해주세요.</p>
          <ul className="operation-list">{(showFailures ? data.failed_statements : data.failed_statements.slice(0, 3)).map(live => <StatementFailure key={live.dispatch_id} live={live} onChanged={message => { setFeedback(message); void refresh(); }} />)}</ul>
          {data.failed_statements.length > 3 && <button type="button" className="show-more" aria-expanded={showFailures} onClick={() => setShowFailures(!showFailures)}>{showFailures ? '접기' : '발송 실패 방송 모두 보기'}</button>}
        </div> : state(data.failed_statements, '정산서 발송 실패가 없습니다.'))}
        </>}
        </div>
      </section>
      <section className="operations-panel reservation-panel" aria-label="예약 라이브">
        <div className="panel-heading"><h2>예약 라이브 <span>{data?.reserved_lives ? count(data.reserved_lives.length) : '—'}</span></h2>
          <button type="button" onClick={() => navigate('/admin/livelist/make')}>라이브 예약하기</button></div>
        {!data?.reserved_lives?.length && state(data?.reserved_lives, '예약된 라이브가 없습니다.')}
        {!!data?.reserved_lives?.length && <><ul className="operation-list reservation-list">{(showAll ? data.reserved_lives : data.reserved_lives.slice(0, 2)).map(live => <li key={live.seq}>
          <button type="button" className="operation-row" onClick={() => navigate(`/admin/livelist/${live.seq}`)}>
            <span><strong title={live.live_title}>{live.live_title}</strong><small>{date(live.live_start)} ~ {new Date(live.live_finish).toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', hour12: false })}</small></span><span className="row-action">바로 진입 →</span>
          </button></li>)}</ul>
          {data.reserved_lives.length > 2 && <button type="button" className="show-more" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>{showAll ? '접기' : `예약 ${count(data.reserved_lives.length)}건 모두 보기`}</button>}
        </>}
      </section>
    </div>
  </OperationsArea>;
}

const OperationsArea = styled.section`
  margin-bottom: var(--space-6);
  color: var(--color-text-primary);
  font-family: var(--font-family-base);
  .operations-heading, .panel-heading { display: flex; align-items: center; gap: var(--space-4); flex-wrap: wrap; }
  .operations-heading, .panel-heading { justify-content: space-between; }
  .operations-heading { margin-bottom: var(--space-4); }
  .operations-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-5); }
  && h2 { font-size: var(--text-h3); line-height: var(--leading-h3); }
  h3 { font-size: var(--text-body-lg); margin: 0; }
  h4 { font-size: var(--text-body); margin: 0 0 var(--space-1); }
  h3, h4 { font-weight: var(--font-weight-semibold); }
  h2 span, h3 span, h4 span { margin-left: var(--space-2); font-variant-numeric: tabular-nums; }
  && p, .period-label { font-size: var(--text-caption); line-height: var(--leading-caption); color: var(--color-text-secondary); }
  .operations-panel { min-width: 0; padding: var(--space-5); background: var(--color-bg-surface); border: 1px solid var(--color-border-default); border-radius: var(--radius-md); }
  && button { min-height: var(--touch-target); font: inherit; font-size: var(--text-caption); font-weight: var(--font-weight-medium); padding: var(--space-2) var(--space-4); border: 1px solid var(--color-border-strong); border-radius: var(--radius-md); background: var(--color-bg-surface); color: var(--color-text-primary); }
  && button:hover:not(:disabled) { background: var(--color-bg-subtle); color: var(--color-text-primary); border-color: var(--color-border-strong); }
  && button:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  && button:disabled { opacity: 1; background: var(--color-bg-muted); color: var(--color-text-disabled); cursor: wait; }
  .alert-tabs { display: flex; flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-4); }
  && .alert-tabs button { display: inline-flex; justify-content: center; align-items: center; gap: var(--space-3); min-height: var(--control-lg); border-color: var(--color-border-default); }
  && .alert-tabs button[aria-selected="true"] { background: var(--color-bg-inverse); color: var(--color-text-on-inverse); border-color: var(--color-bg-inverse); }
  .tab-count { font-variant-numeric: tabular-nums; white-space: nowrap; }
  .failure-count { color: var(--color-danger-strong); background: var(--color-danger-subtle); border-radius: var(--radius-sm); padding: var(--space-1) var(--space-2); }
  .operation-list { list-style: none; padding: 0; margin: var(--space-2) 0 0; }
  && button.operation-row { width: 100%; display: flex; justify-content: space-between; align-items: center; gap: var(--space-4); text-align: left; border: 0; border-bottom: 1px solid var(--color-border-default); border-radius: 0; padding: var(--space-3) 0; }
  .operation-row > span:first-child { min-width: 0; flex: 1; display: flex; align-items: center; gap: var(--space-6); }
  .operations-grid .operation-row > span:first-child { flex-direction: column; align-items: flex-start; gap: var(--space-1); }
  .operations-grid .operation-row strong { max-width: 100%; flex: none; }
  .operation-row strong { min-width: 0; flex: 1; font-size: var(--text-body); font-weight: var(--font-weight-medium); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .operation-row small { flex-shrink: 0; font-size: var(--text-caption); color: var(--color-text-secondary); line-height: var(--leading-caption); }
  .row-action { flex-shrink: 0; min-width: 9em; text-align: right; font-size: var(--text-caption); color: var(--color-text-link); font-variant-numeric: tabular-nums; }
  .inquiry, .alert-group { margin-top: var(--space-4); }
  .section-state { padding: var(--space-5) 0; }
  && .operations-error { padding: var(--space-3); margin-bottom: var(--space-4); background: var(--color-danger-subtle); color: var(--color-danger-strong); border-radius: var(--radius-md); }
  .operations-feedback { padding: var(--space-3) 0; }
  .failure-detail { background: var(--color-bg-subtle); padding: var(--space-4); border-radius: var(--radius-md); overflow-wrap: anywhere; }
  .failure-detail ul { padding-left: var(--space-5); font-size: var(--text-caption); line-height: var(--leading-body); max-height: 180px; overflow-y: auto; }
  .failure-detail p { margin-bottom: var(--space-3); }
  .detail-actions, .confirmation { display: flex; flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-3); }
  .confirmation p { flex-basis: 100%; }
  && button.show-more { margin-top: var(--space-3); border-color: var(--color-border-default); }
  @media (max-width: 1000px) { .operations-grid { grid-template-columns: minmax(0, 1fr); } }
  @media (max-width: 540px) {
    .operations-panel { padding: var(--space-4); }
    .alert-tabs { display: grid; grid-template-columns: 1fr; }
    && .alert-tabs button { justify-content: space-between; }
    && button.operation-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; column-gap: var(--space-2); row-gap: var(--space-1); }
    .operation-row > span:first-child { display: contents; }
    .operation-row strong { grid-column: 1 / -1; white-space: normal; overflow-wrap: anywhere; }
    .operation-row small { grid-column: 1; }
    .row-action { grid-column: 2; grid-row: 2; min-width: 0; }
  }
`;
